import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCustomerDebtReport, getSettings } from '../api/client';
import { rivhitDisplayName, type RivhitCustomer } from '../context/RivhitDataContext';

// ── Types ────────────────────────────────────────────────────────────

interface CustomerDebtItemSummary {
  product_id: string;
  product_name: string;
  total_quantity: number;
  order_count: number;
  typical_unit: string;
  last_ordered_at?: string | null;
  last_price?: number | null;
  avg_monthly_3m?: number | null;
  avg_monthly_6m?: number | null;
}

interface CustomerDebtMonth {
  month: string;
  invoices_amount: number;
  paid_amount: number;
  unpaid_amount: number;
  due_date: string;
  status: 'paid' | 'overdue' | 'due_soon' | 'current';
}

interface QuarterMonthAmount {
  month: string;
  quantity: number;
}

interface QuarterItemSummary {
  product_id: string;
  product_name: string;
  typical_unit: string;
  total_quantity: number;
  last_price?: number | null;
  monthly_amounts: QuarterMonthAmount[];
}

interface CustomerRecentDocument {
  document_type: number;
  document_type_name: string;
  document_number: number;
  document_date: string;
  amount: number;
  document_link?: string | null;
}

export interface CustomerDebtDetail {
  customer_id: number;
  customer_name: string;
  phone?: string | null;
  email?: string | null;
  period: string;
  from_date: string;
  to_date: string;
  payment_terms_days: number;
  current_balance: number;
  invoices_amount: number;
  paid_amount: number;
  unpaid_amount: number;
  aged_debt_amount: number;
  overdue_amount: number;
  items: CustomerDebtItemSummary[];
  monthly_debt: CustomerDebtMonth[];
  recent_order_from_date?: string | null;
  recent_order_to_date?: string | null;
  recent_order_months?: string[];
  recent_order_items?: QuarterItemSummary[];
  recent_documents?: CustomerRecentDocument[];
}

// ── Props ────────────────────────────────────────────────────────────

interface CustomerDetailsModalProps {
  /** Full RivhitCustomer when available (Customers / Documents pages). */
  customer?: RivhitCustomer;
  /** Minimal fallback: at least a customer_id is required when customer is not available. */
  customerId?: number;
  /** Display name fallback when no RivhitCustomer is provided. */
  displayName?: string;
  onClose: () => void;
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatNIS(amount: number): string {
  return `₪${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatDate(value?: string | null, locale?: string): string {
  if (!value) return '—';
  const d = new Date(`${value.slice(0, 10)}T00:00:00`);
  return d.toLocaleDateString(locale || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatMonthLabel(month: string, locale?: string): string {
  const d = new Date(`${month}-01T00:00:00`);
  return d.toLocaleDateString(locale || undefined, { year: 'numeric', month: 'short' });
}

function normalizePhone(phone?: string | null): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('0')) return `972${digits.slice(1)}`;
  return digits;
}

function LoadingValue({ className = 'h-8 w-24' }: { className?: string }) {
  return <span className={`inline-block animate-pulse rounded-md bg-gray-200 ${className}`} />;
}

const STATUS_CLASSES: Record<string, string> = {
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  due_soon: 'bg-amber-100 text-amber-700',
  current: 'bg-slate-100 text-slate-700',
};

// ── Component ────────────────────────────────────────────────────────

export default function CustomerDetailsModal({
  customer,
  customerId: externalCustomerId,
  displayName: externalDisplayName,
  onClose,
}: CustomerDetailsModalProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const resolvedId = customer?.customer_id ?? externalCustomerId ?? 0;
  const resolvedName = customer ? rivhitDisplayName(customer) : externalDisplayName || `#${resolvedId}`;

  const [detail, setDetail] = useState<CustomerDebtDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debtAgeThresholdDays, setDebtAgeThresholdDays] = useState(60);

  // Load settings once
  useEffect(() => {
    getSettings()
      .then((res) => {
        const v = Number(res.data?.debt_age_threshold_days ?? res.data?.settings?.debt_age_threshold_days ?? 60);
        setDebtAgeThresholdDays(Number.isFinite(v) && v > 0 ? v : 60);
      })
      .catch(() => setDebtAgeThresholdDays(60));
  }, []);

  // Fetch customer debt report
  useEffect(() => {
    if (!resolvedId) return;
    let active = true;
    setLoading(true);
    setError(null);

    getCustomerDebtReport(resolvedId)
      .then((res) => { if (active) setDetail(res.data); })
      .catch(() => { if (active) { setDetail(null); setError(t('customers.detailsError')); } })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [resolvedId, t]);

  // Derived data
  const phone = detail?.phone || customer?.phone;
  const email = detail?.email || customer?.email;
  const paymentDays = detail?.payment_terms_days || debtAgeThresholdDays;
  const hasDebt = (detail?.unpaid_amount ?? 0) > 0;

  // WhatsApp debt message (only when there is outstanding debt)
  const debtMessage = useMemo(() => {
    if (!detail || !hasDebt) return '';
    const name = detail.customer_name || resolvedName;
    const monthlyLines = detail.monthly_debt?.length
      ? detail.monthly_debt
          .map((m) => t('reports.debtMessageMonthLine', { month: formatMonthLabel(m.month, locale), amount: formatNIS(m.unpaid_amount) }))
          .join('\n')
      : '';
    return t('reports.debtMessageTemplate', {
      customer: name,
      unpaid: formatNIS(detail.unpaid_amount),
      overdue: formatNIS(detail.overdue_amount),
      monthlyDebt: monthlyLines,
    });
  }, [detail, locale, resolvedName, t]);

  const handleCopyMessage = useCallback(async () => {
    if (!debtMessage) return;
    try { await navigator.clipboard.writeText(debtMessage); } catch { /* ignore */ }
  }, [debtMessage]);

  const handleSendMessage = useCallback(() => {
    const normalized = normalizePhone(phone);
    if (!normalized || !debtMessage) return;
    window.open(`https://wa.me/${normalized}?text=${encodeURIComponent(debtMessage)}`, '_blank', 'noopener,noreferrer');
  }, [phone, debtMessage]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/45 p-4" onClick={onClose}>
      <div
        className="w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-100 bg-white/95 px-6 py-5 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-lg font-bold text-primary-700">
              {resolvedName.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-sm font-medium text-primary-600">{t('reports.customerDebtTitle')}</p>
              <h2 className="text-xl font-bold text-gray-900">{resolvedName}</h2>
              <p className="text-sm text-gray-500">#{resolvedId}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500 hover:bg-gray-50">
            {t('common.close', 'Close')}
          </button>
        </div>

        <div className="space-y-6 px-6 py-6">
          {/* ── Contact info ── */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 text-sm">
            {phone && (
              <div>
                <span className="font-medium text-gray-500">{t('customers.phone')}</span>
                <p className="mt-0.5 text-gray-900">{phone}</p>
              </div>
            )}
            {email && (
              <div>
                <span className="font-medium text-gray-500">{t('customers.email')}</span>
                <p className="mt-0.5 text-gray-900">{email}</p>
              </div>
            )}
            {customer?.street && (
              <div>
                <span className="font-medium text-gray-500">{t('customers.street', 'Street')}</span>
                <p className="mt-0.5 text-gray-900">{customer.street}</p>
              </div>
            )}
            {customer?.city && (
              <div>
                <span className="font-medium text-gray-500">{t('customers.city')}</span>
                <p className="mt-0.5 text-gray-900">{customer.city}</p>
              </div>
            )}
            {customer?.id_number != null && customer.id_number !== 0 && (
              <div>
                <span className="font-medium text-gray-500">{t('customers.idNumber', 'ID Number')}</span>
                <p className="mt-0.5 text-gray-900">{customer.id_number}</p>
              </div>
            )}
            {customer?.vat_number != null && customer.vat_number !== 0 && (
              <div>
                <span className="font-medium text-gray-500">{t('customers.vatNumber', 'VAT Number')}</span>
                <p className="mt-0.5 text-gray-900">{customer.vat_number}</p>
              </div>
            )}
          </div>

          {/* ── KPI cards ── */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="text-sm font-medium text-amber-700">{t('reports.unpaidAmount')}</div>
              <div className="mt-2 text-2xl font-bold text-amber-900">
                {loading ? <LoadingValue className="h-8 w-28 bg-amber-200/70" /> : formatNIS(detail?.unpaid_amount ?? 0)}
              </div>
            </div>
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
              <div className="text-sm font-medium text-rose-700">{t('reports.agedDebtAmount', { days: paymentDays })}</div>
              <div className="mt-2 text-2xl font-bold text-rose-900">
                {loading ? <LoadingValue className="h-8 w-28 bg-rose-200/70" /> : formatNIS(detail?.aged_debt_amount ?? 0)}
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          )}

          {/* ── Payment terms ── */}
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="text-sm text-gray-600">
              {t('reports.paymentTerms', { days: paymentDays })}
            </div>
            {loading ? (
              <LoadingValue className="h-5 w-32" />
            ) : phone ? (
              <div className="text-sm text-gray-500">{phone}</div>
            ) : null}
            {loading ? (
              <LoadingValue className="h-5 w-40" />
            ) : email ? (
              <div className="text-sm text-gray-500">{email}</div>
            ) : null}
          </div>

          {/* ── Message preview (only when debt exists) ── */}
          {hasDebt && debtMessage && !loading && (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-700">{t('reports.messagePreview')}</div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleCopyMessage}
                    disabled={loading || !debtMessage}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('reports.copyMessage')}
                  </button>
                  <button
                    onClick={handleSendMessage}
                    disabled={!phone || loading || !debtMessage}
                    className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {t('reports.sendMessage')}
                  </button>
                </div>
              </div>
              <div className="mt-3 whitespace-pre-wrap rounded-lg bg-gray-50 p-4 text-sm leading-6 text-gray-700">
                {debtMessage}
              </div>
            </div>
          )}

          {/* ── Items consumed (top items all-time) + Monthly debt ── */}
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">{t('reports.loadingCustomerDetails')}</div>
          ) : (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_0.9fr]">
              {/* Items consumed */}
              <div className="rounded-xl border border-gray-100 bg-white">
                <div className="border-b border-gray-100 px-5 py-4">
                  <h3 className="text-base font-semibold text-gray-900">{t('reports.itemsConsumed')}</h3>
                </div>
                {detail?.items?.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-gray-500">
                          <th className="px-5 py-3 text-start font-medium">{t('reports.item')}</th>
                          <th className="px-5 py-3 text-end font-medium">{t('reports.avgMonthly3m')}</th>
                          <th className="px-5 py-3 text-end font-medium">{t('reports.avgMonthly6m')}</th>
                          <th className="px-5 py-3 text-end font-medium">{t('reports.lastPrice')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.items.map((item) => (
                          <tr key={item.product_id} className="border-t border-gray-50">
                            <td className="px-5 py-3 font-medium text-gray-800">{item.product_name}</td>
                            <td className="px-5 py-3 text-end text-gray-600">
                              {item.avg_monthly_3m != null ? `${item.avg_monthly_3m.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${item.typical_unit}` : '—'}
                            </td>
                            <td className="px-5 py-3 text-end text-gray-600">
                              {item.avg_monthly_6m != null ? `${item.avg_monthly_6m.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${item.typical_unit}` : '—'}
                            </td>
                            <td className="px-5 py-3 text-end text-gray-600">
                              {item.last_price != null ? formatNIS(item.last_price) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-5 py-8 text-center text-sm text-gray-400">{t('reports.noRelevantItems')}</div>
                )}
              </div>

              {/* Monthly debt */}
              <div className="rounded-xl border border-gray-100 bg-white">
                <div className="border-b border-gray-100 px-5 py-4">
                  <h3 className="text-base font-semibold text-gray-900">{t('reports.monthlyDebt')}</h3>
                </div>
                {detail?.monthly_debt?.length ? (
                  <div className="space-y-3 px-5 py-4">
                    {detail.monthly_debt.map((month) => (
                      <div key={month.month} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="font-semibold text-gray-900">{formatMonthLabel(month.month, locale)}</div>
                            <div className="text-xs text-gray-500">
                              {t('reports.dueDate')}: {formatDate(month.due_date, locale)}
                            </div>
                          </div>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CLASSES[month.status] || ''}`}>
                            {t(`reports.status.${month.status}`)}
                          </span>
                        </div>
                        <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                          <div>
                            <div className="text-gray-500">{t('reports.invoicesAmount')}</div>
                            <div className="mt-1 font-semibold text-gray-900">{formatNIS(month.invoices_amount)}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">{t('reports.paidAmount')}</div>
                            <div className="mt-1 font-semibold text-green-700">{formatNIS(month.paid_amount)}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">{t('reports.unpaidAmount')}</div>
                            <div className="mt-1 font-semibold text-amber-700">{formatNIS(month.unpaid_amount)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-5 py-8 text-center text-sm text-gray-400">{t('reports.noData')}</div>
                )}
              </div>
            </div>
          )}

          {/* ── Recent quarter items ── */}
          {!loading && detail?.recent_order_items?.length ? (
            <div className="rounded-xl border border-gray-100 bg-white">
              <div className="border-b border-gray-100 px-5 py-4">
                <h3 className="text-base font-semibold text-gray-900">{t('customers.recentItemsTitle')}</h3>
                {detail.recent_order_from_date && detail.recent_order_to_date && (
                  <p className="mt-1 text-xs text-gray-500">
                    {t('customers.recentPeriod', {
                      from: formatDate(detail.recent_order_from_date, locale),
                      to: formatDate(detail.recent_order_to_date, locale),
                    })}
                  </p>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500">
                      <th className="px-5 py-3 text-start font-medium">{t('reports.item')}</th>
                      {(detail.recent_order_months || []).map((m) => (
                        <th key={m} className="px-4 py-3 text-end font-medium">{formatMonthLabel(m, locale)}</th>
                      ))}
                      <th className="px-5 py-3 text-end font-medium">{t('customers.totalQuantity')}</th>
                      <th className="px-5 py-3 text-end font-medium">{t('customers.itemPrice')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.recent_order_items.map((item) => (
                      <tr key={item.product_id || item.product_name} className="border-t border-gray-50">
                        <td className="px-5 py-3 font-medium text-gray-800">{item.product_name}</td>
                        {(detail.recent_order_months || []).map((m) => {
                          const qty = item.monthly_amounts?.find((a) => a.month === m)?.quantity ?? 0;
                          return (
                            <td key={m} className="px-4 py-3 text-end text-gray-600">
                              {qty > 0 ? qty.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}
                            </td>
                          );
                        })}
                        <td className="px-5 py-3 text-end font-semibold text-gray-800">
                          {item.total_quantity.toLocaleString(undefined, { maximumFractionDigits: 1 })} {item.typical_unit}
                        </td>
                        <td className="px-5 py-3 text-end text-gray-600">
                          {item.last_price != null ? formatNIS(item.last_price) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {/* ── Recent documents ── */}
          {!loading && detail?.recent_documents?.length ? (
            <div className="rounded-xl border border-gray-100 bg-white">
              <div className="border-b border-gray-100 px-5 py-4">
                <h3 className="text-base font-semibold text-gray-900">{t('reports.recentDocuments')}</h3>
              </div>
              <div className="divide-y divide-gray-50">
                {detail.recent_documents.map((doc) => (
                  <div key={`${doc.document_type}-${doc.document_number}`} className="flex items-center justify-between gap-3 px-5 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="min-w-0">
                        {doc.document_link ? (
                          <a
                            href={doc.document_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-primary-600 hover:text-primary-800 hover:underline"
                          >
                            {doc.document_type_name || `#${doc.document_type}`} #{doc.document_number}
                          </a>
                        ) : (
                          <span className="font-medium text-gray-800">
                            {doc.document_type_name || `#${doc.document_type}`} #{doc.document_number}
                          </span>
                        )}
                        <p className="text-xs text-gray-500">{formatDate(doc.document_date, locale)}</p>
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">{formatNIS(doc.amount)}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
